import { type SanityReference } from '@sanity/client'
import {
  type Dispatch,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { type SanityFrameBuilderDefaultFrame } from '@frame-builder/data/sanity/queries/types/default-frame'
import {
  useAutomaticCommittedStateUpdate,
  useAutomaticSelectedStateUpdate,
} from '@frame-builder/lib/state-automatic-save'
import { getFrameBuilderDefaultState } from '@frame-builder/lib/default-state'
import {
  type FrameBuilderStateAction,
  frameBuilderStateReducer,
} from '@frame-builder/lib/state-reducer'
import {
  getFrameString,
  getStateActionsFromFrameString,
} from '@frame-builder/lib/state-serialization'
import { type FrameBuilderState } from '@frame-builder/lib/types'
import { FrameBuilderSettingsContext } from './settings-context'

interface FrameBuilderCommittedStateContextProps {
  committedState: FrameBuilderState
  dispatchCommittedState: Dispatch<FrameBuilderStateAction>
}

interface FrameBuilderSelectedStateContextProps {
  selectedState: FrameBuilderState
  dispatchSelectedState: Dispatch<FrameBuilderStateAction>
  isSelectedStateLoaded: boolean
}

interface FrameBuilderDefaultStateContextProps {
  defaultState: FrameBuilderState
}

interface FrameBuilderStateProviderProps {
  defaultFrame: SanityFrameBuilderDefaultFrame | null
  defaultPassepartoutColor?: SanityReference
  defaultPassepartoutWidth?: number
  defaultPassepartoutHeight?: number
  updateHash: boolean
  children: ReactNode
}

export const emptyFrameBuilderState: FrameBuilderState = {
  artwork: {
    width: 0,
    height: 0,
  },
  passepartout: {},
}

export const FrameBuilderCommittedStateContext =
  createContext<FrameBuilderCommittedStateContextProps>({
    committedState: emptyFrameBuilderState,
    dispatchCommittedState: () => {},
  })

export const FrameBuilderSelectedStateContext =
  createContext<FrameBuilderSelectedStateContextProps>({
    selectedState: emptyFrameBuilderState,
    dispatchSelectedState: () => {},
    isSelectedStateLoaded: false,
  })

export const FrameBuilderDefaultStateContext =
  createContext<FrameBuilderDefaultStateContextProps>({
    defaultState: emptyFrameBuilderState,
  })

export const FrameBuilderStateProvider = ({
  defaultFrame,
  defaultPassepartoutColor,
  defaultPassepartoutWidth,
  defaultPassepartoutHeight,
  updateHash,
  children,
}: FrameBuilderStateProviderProps) => {
  const { frameBuilderConfiguration, frameBuilderSettings, frameBuilderType } =
    useContext(FrameBuilderSettingsContext)

  const [isSelectedStateLoaded, setIsSelectedStateLoaded] = useState(false)

  const isMounted = useRef(false)

  const defaultState = useMemo(() => {
    return getFrameBuilderDefaultState(
      frameBuilderType,
      frameBuilderConfiguration,
      frameBuilderSettings,
      defaultFrame,
      defaultPassepartoutColor,
      defaultPassepartoutWidth,
      defaultPassepartoutHeight,
    )
  }, [
    defaultFrame,
    defaultPassepartoutColor,
    defaultPassepartoutHeight,
    defaultPassepartoutWidth,
    frameBuilderConfiguration,
    frameBuilderSettings,
    frameBuilderType,
  ])

  const [committedState, dispatchCommittedState] = useReducer(
    frameBuilderStateReducer,
    {
      ...emptyFrameBuilderState,
      ...defaultState,
    },
  )
  const [selectedState, dispatchSelectedState] = useReducer(
    frameBuilderStateReducer,
    {
      ...emptyFrameBuilderState,
      ...defaultState,
    },
  )

  useAutomaticCommittedStateUpdate(selectedState, dispatchCommittedState)
  useAutomaticSelectedStateUpdate(
    frameBuilderConfiguration,
    selectedState,
    dispatchSelectedState,
  )

  // Update hash in URL after changes in committed state
  useEffect(() => {
    if (!isMounted.current || !updateHash) {
      return
    }

    const frameString = getFrameString(committedState)
    window.location.hash = frameString
  }, [committedState, updateHash])

  // Load current frame state from the URL
  useEffect(() => {
    if (isSelectedStateLoaded) {
      return
    }

    const frameString =
      typeof window !== 'undefined'
        ? window.location.hash?.replace(/^[#]{1}/g, '')
        : null

    if (frameString) {
      const frameStringStateActions = getStateActionsFromFrameString(
        frameBuilderConfiguration,
        defaultState,
        frameString,
      )
      frameStringStateActions.forEach((frameStringStateAction) => {
        dispatchSelectedState(frameStringStateAction)
      })
    }

    setIsSelectedStateLoaded(true)
  }, [
    defaultState,
    dispatchSelectedState,
    frameBuilderConfiguration,
    isSelectedStateLoaded,
  ])

  useEffect(() => {
    isMounted.current = true
  }, [])

  return (
    <FrameBuilderCommittedStateContext.Provider
      value={{
        committedState,
        dispatchCommittedState,
      }}
    >
      <FrameBuilderSelectedStateContext.Provider
        value={{
          selectedState,
          dispatchSelectedState,
          isSelectedStateLoaded,
        }}
      >
        <FrameBuilderDefaultStateContext.Provider
          value={{
            defaultState,
          }}
        >
          {children}
        </FrameBuilderDefaultStateContext.Provider>
      </FrameBuilderSelectedStateContext.Provider>
    </FrameBuilderCommittedStateContext.Provider>
  )
}
